import moment from 'moment';
import React, { useEffect, useState } from 'react';

import CustodianAgreementInterface from '@api/requests/applications/interfaces/CustodianAgreementInterface.interface';

import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, Button, CircularProgress, Dialog, Divider, IconButton, Toolbar, Typography } from '@mui/material';

import FolderIcon from 'jsx:@assets/svgs/folder.svg';

import { CUSTODIAN_AGREEMENTS_TEXTS } from './constants';
import {
	ModalContent,
	ModalPreview,
	ModalSidebar,
	PreviewActions,
	PreviewContent,
	PreviewPlaceholder,
	SidebarItem,
} from './styles';
import {
	getAgreementMandatorityLabel,
	getAgreementStatusIcon,
	getAgreementStatusLabel,
	isAgreementSigned,
} from './utils';

interface CustodianAgreementModalProps {
	isOpen: boolean;
	onClose: () => void;
	agreements: CustodianAgreementInterface[];
	selectedAgreement: CustodianAgreementInterface | null;
	onSelect: (agreement: CustodianAgreementInterface) => void;
	onAgree: (agreement: CustodianAgreementInterface) => void;
	agreeingId: number | null;
}

function CustodianAgreementModal({
	isOpen,
	onClose,
	agreements,
	selectedAgreement,
	onSelect,
	onAgree,
	agreeingId,
}: CustodianAgreementModalProps) {
	const [hasLoadError, setHasLoadError] = useState(false);

	useEffect(() => {
		setHasLoadError(false);
	}, [selectedAgreement?.id]);

	const isSelectedSigned = selectedAgreement ? isAgreementSigned(selectedAgreement) : false;
	const isSelectedAgreeing = selectedAgreement ? agreeingId === selectedAgreement.id : false;
	const canShowDocument = Boolean(selectedAgreement?.uri) && !hasLoadError;

	return (
		<Dialog fullScreen open={isOpen} onClose={onClose}>
			<AppBar position="sticky" color="default" elevation={0}>
				<Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
					<Typography variant="h6" sx={{ fontSize: { xs: 18, sm: 20 } }}>
						<b>{CUSTODIAN_AGREEMENTS_TEXTS.sectionTitle}</b>
					</Typography>

					<IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
						<CloseIcon />
					</IconButton>
				</Toolbar>
			</AppBar>

			<ModalContent>
				<ModalSidebar>
					{agreements.map(agreement => {
						const mandatority = getAgreementMandatorityLabel(agreement);

						return (
							<SidebarItem
								key={agreement.id}
								isActive={selectedAgreement?.id === agreement.id}
								onClick={() => onSelect(agreement)}
							>
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
										{agreement.name}
									</Typography>
									<Typography variant="caption" color="textSecondary">
										{getAgreementStatusLabel(agreement)}
										{mandatority ? ` \u2022 ${mandatority}` : ''}
									</Typography>
								</Box>

								<Box sx={{ display: 'flex', flexShrink: 0 }}>{getAgreementStatusIcon(agreement)}</Box>
							</SidebarItem>
						);
					})}
				</ModalSidebar>

				<ModalPreview>
					<PreviewContent>
						{canShowDocument && selectedAgreement ? (
							<iframe
								title={selectedAgreement.name}
								src={selectedAgreement.uri}
								onError={() => setHasLoadError(true)}
							/>
						) : (
							<PreviewPlaceholder>
								<FolderIcon />
								<Typography variant="body2" color="textSecondary">
									{CUSTODIAN_AGREEMENTS_TEXTS.documentUnavailable}
								</Typography>
							</PreviewPlaceholder>
						)}
					</PreviewContent>

					{selectedAgreement ? (
						<PreviewActions>
							{isSelectedSigned ? (
								<Typography variant="caption" color="textSecondary">
									{selectedAgreement.agreedAt
										? `Signed on ${moment(selectedAgreement.agreedAt).format('MMM Do, YYYY [at] h:mm A')}`
										: CUSTODIAN_AGREEMENTS_TEXTS.agreedActionDone}
								</Typography>
							) : (
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={() => onAgree(selectedAgreement)}
									disabled={isSelectedAgreeing}
									sx={{ textTransform: 'initial', minWidth: 120, height: 32 }}
								>
									{isSelectedAgreeing ? (
										<CircularProgress size={15} color="info" variant="indeterminate" />
									) : (
										<Typography variant="caption">
											<b>{CUSTODIAN_AGREEMENTS_TEXTS.agreeAction}</b>
										</Typography>
									)}
								</Button>
							)}
						</PreviewActions>
					) : (
						<Divider />
					)}
				</ModalPreview>
			</ModalContent>
		</Dialog>
	);
}

export default CustodianAgreementModal;
